import React, { useState, useRef, useEffect, useCallback } from 'react'
import { supabase } from '../supabaseClient'
import { lsDeriveKey, lsEncrypt, lsDecrypt } from '../cryptoUtils'

// ─── Storage ─────────────────────────────────────────────────────────────────
const STORAGE_KEY = 'bops-mm-data'
const SAVE_DEBOUNCE_MS = 600

let _cachedKey = null
let _cachedKeyUserId = null

async function mmGetKey() {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) return null
  if (_cachedKey && _cachedKeyUserId === session.user.id) return _cachedKey
  _cachedKey = await lsDeriveKey(session.user.id)
  _cachedKeyUserId = session.user.id
  return _cachedKey
}

async function mmLoad() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const key = await mmGetKey()
    if (!key) return null
    try {
      return await lsDecrypt(key, raw)
    } catch {
      return JSON.parse(raw)
    }
  } catch { return null }
}

async function mmSave(data) {
  try {
    const key = await mmGetKey()
    if (key) {
      const enc = await lsEncrypt(key, data)
      localStorage.setItem(STORAGE_KEY, enc)
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    }
  } catch {}
}

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS = {
  ACTIVE:   '#00E5A0',
  LIVE:     '#00E5A0',
  BUILDING: '#FFD166',
  PLANNED:  '#6B7080',
  FUTURE:   '#3A3E4A',
  OPEN:     '#3A3E4A',
}

// ─── Node dimensions ──────────────────────────────────────────────────────────
const DIM = {
  root:     { w: 168, h: 96 },
  branch:   { w: 138, h: 80 },
  division: { w: 118, h: 70 },
  leaf:     { w: 104, h: 62 },
}

// ─── Default tree ─────────────────────────────────────────────────────────────
const DEFAULT_TREE = {
  id: 'group', name: 'BLESSEDOPS\nGROUP', sub: 'Kingsley Olukanni - Founder', type: 'root', status: 'ACTIVE', expanded: true,
  children: [
    {
      id: 'ventures', name: 'Ventures', sub: 'Revenue divisions', type: 'branch', status: 'ACTIVE', expanded: true,
      children: [
        {
          id: 'securva', name: 'Securva', sub: 'SMB security platform', type: 'division', status: 'LIVE', expanded: false,
          children: [
            { id: 'sec-scanner', name: 'Scanner', sub: 'securva.net', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
            { id: 'sec-vendor', name: 'Vendor\nSweep', sub: 'B2B vendor audit', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
            { id: 'sec-research', name: 'Research\nPortal', sub: '/research deep-dives', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
            { id: 'sec-tin', name: 'TIN-Verify', sub: 'Snapshot Pro section', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
            { id: 'sec-disclosures', name: 'Disclosures', sub: 'Goodwill outreach', type: 'leaf', status: 'ACTIVE', expanded: false, children: [] },
            { id: 'sec-v2', name: 'Securva v2', sub: 'Continuous audit (planned)', type: 'leaf', status: 'PLANNED', expanded: false, children: [] },
          ],
        },
        {
          id: 'pejji', name: 'Pejji', sub: 'Nigerian SMB security', type: 'division', status: 'LIVE', expanded: false,
          children: [
            { id: 'pejji-com', name: 'pejji.com', sub: 'Primary site', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
            { id: 'pejji-ng', name: 'pejji.ng', sub: 'NG-TLD mirror', type: 'leaf', status: 'BUILDING', expanded: false, children: [] },
            { id: 'pejji-quiz', name: 'Quiz\nFunnel', sub: 'Lead capture', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
            { id: 'pejji-outreach', name: 'SMB\nOutreach', sub: 'Cold + reactive', type: 'leaf', status: 'ACTIVE', expanded: false, children: [] },
          ],
        },
        {
          id: 'bounty', name: 'Bug Bounty\nPractice', sub: '90-day income arm', type: 'division', status: 'ACTIVE', expanded: false,
          children: [
            { id: 'b-ywh', name: 'YWH', sub: 'Veygo Critical pending', type: 'leaf', status: 'ACTIVE', expanded: false, children: [] },
            { id: 'b-bc', name: 'Bugcrowd', sub: 'Auth0 / Sophos / Fivetran', type: 'leaf', status: 'ACTIVE', expanded: false, children: [] },
            { id: 'b-immunefi', name: 'Immunefi', sub: 'Aster pending', type: 'leaf', status: 'ACTIVE', expanded: false, children: [] },
            { id: 'b-h1', name: 'HackerOne', sub: 'Brave / ENS / Cash App', type: 'leaf', status: 'PLANNED', expanded: false, children: [] },
          ],
        },
        {
          id: 'media', name: 'BlessedOps\nMedia', sub: 'Content engine', type: 'division', status: 'ACTIVE', expanded: false,
          children: [
            { id: 'm-bip', name: 'Build-in\nPublic', sub: 'Day 25/90', type: 'leaf', status: 'ACTIVE', expanded: false, children: [] },
            { id: 'm-x', name: 'X / Twitter', sub: '@babakizo', type: 'leaf', status: 'ACTIVE', expanded: false, children: [] },
            { id: 'm-li', name: 'LinkedIn', sub: 'Securva brand', type: 'leaf', status: 'ACTIVE', expanded: false, children: [] },
            { id: 'm-yt', name: 'YouTube', sub: 'Walkthroughs', type: 'leaf', status: 'PLANNED', expanded: false, children: [] },
          ],
        },
        {
          id: 'future-ventures', name: 'Future\nVentures', sub: 'Open slots', type: 'division', status: 'OPEN', expanded: false,
          children: [
            { id: 'fv-rentals', name: 'BlessedOps\nRentals', sub: 'Real estate / assets', type: 'leaf', status: 'FUTURE', expanded: false, children: [] },
            { id: 'fv-armor', name: 'Cyber\nArmor', sub: 'R&D / Web3', type: 'leaf', status: 'FUTURE', expanded: false, children: [] },
            { id: 'fv-vault', name: 'Utility\nVault', sub: 'Productized tools', type: 'leaf', status: 'FUTURE', expanded: false, children: [] },
            { id: 'fv-investments', name: 'Investments\n& Holdings', sub: 'Diversified portfolio', type: 'leaf', status: 'FUTURE', expanded: false, children: [] },
          ],
        },
      ],
    },
    {
      id: 'arsenal', name: 'Arsenal', sub: 'Toolset / methodology', type: 'branch', status: 'ACTIVE', expanded: true,
      children: [
        {
          id: 'recon', name: 'Recon\nStack', sub: 'Discovery tools', type: 'division', status: 'ACTIVE', expanded: false,
          children: [
            { id: 'r-net', name: 'Network', sub: 'amass / subfinder / asnmap / mapcidr / httpx / nuclei', type: 'leaf', status: 'ACTIVE', expanded: false, children: [] },
            { id: 'r-web', name: 'Web', sub: 'Caido / dork-runner v4.9 / cognito-scanner', type: 'leaf', status: 'ACTIVE', expanded: false, children: [] },
            { id: 'r-mobile', name: 'Mobile', sub: 'jadx / apkleaks / Frida / Objection / gplaydl / Genymotion', type: 'leaf', status: 'ACTIVE', expanded: false, children: [] },
            { id: 'r-cloud', name: 'Cloud', sub: 's3scanner / Azure Blob / Shodan / Censys', type: 'leaf', status: 'ACTIVE', expanded: false, children: [] },
            { id: 'r-jwt', name: 'JWT Suite', sub: 'jwt_tool integrated', type: 'leaf', status: 'ACTIVE', expanded: false, children: [] },
          ],
        },
        {
          id: 'methodology', name: 'Methodology', sub: 'The moat', type: 'division', status: 'ACTIVE', expanded: false,
          children: [
            { id: 'mt-patternlab', name: 'Pattern-Lab', sub: '61 entries banked', type: 'leaf', status: 'ACTIVE', expanded: false, children: [] },
            { id: 'mt-skeptic', name: 'Skeptic\nGates', sub: '12 HARD RULES banked', type: 'leaf', status: 'ACTIVE', expanded: false, children: [] },
            { id: 'mt-hsr', name: 'Hunter-Skeptic\n-Referee', sub: 'Adversarial pattern', type: 'leaf', status: 'ACTIVE', expanded: false, children: [] },
            { id: 'mt-mobile', name: 'Mobile Audit\nMethodology v1', sub: 'Cordova / hybrid CSPT', type: 'leaf', status: 'ACTIVE', expanded: false, children: [] },
            { id: 'mt-redteamer', name: 'Redteamer\nPOV Gate', sub: 'Pre-submit HARD RULE', type: 'leaf', status: 'ACTIVE', expanded: false, children: [] },
          ],
        },
        {
          id: 'pipelines', name: 'Pipelines', sub: 'Auto-running ops', type: 'division', status: 'LIVE', expanded: false,
          children: [
            { id: 'p-news', name: 'News\nMonitor', sub: 'cron */30, 9 NG feeds', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
            { id: 'p-snapshot', name: 'Snapshot\nPipeline', sub: 'cron */5, 4 health checks', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
            { id: 'p-web3', name: 'Web3 Audit\nMonitor', sub: 'cron 4h, 5 platforms', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
            { id: 'p-phaseintel', name: 'Phase-Intel\nDB', sub: '91 NG orgs', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
            { id: 'p-monitors', name: 'Bounty\nMonitors', sub: 'Auth0 / Veygo / Aster', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
            { id: 'p-tg', name: 'Telegram\nAlerts', sub: 'Buddy + native channels', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
          ],
        },
        {
          id: 'knowledge', name: 'Knowledge', sub: 'Banked context', type: 'division', status: 'ACTIVE', expanded: false,
          children: [
            { id: 'k-memory', name: 'Memory\nSystem', sub: '133+ files, ~/.claude/memory/', type: 'leaf', status: 'ACTIVE', expanded: false, children: [] },
            { id: 'k-scope', name: 'Scope-Index\nDB', sub: '600+ targets', type: 'leaf', status: 'ACTIVE', expanded: false, children: [] },
            { id: 'k-brand', name: 'Brand\nPalette', sub: 'Pejji + Securva specs', type: 'leaf', status: 'ACTIVE', expanded: false, children: [] },
            { id: 'k-notion', name: '90-Day\nNotion', sub: 'EOD log tracker', type: 'leaf', status: 'ACTIVE', expanded: false, children: [] },
            { id: 'k-frida', name: 'Frida\nCookbook', sub: 'eMAPT recipes', type: 'leaf', status: 'ACTIVE', expanded: false, children: [] },
          ],
        },
      ],
    },
    {
      id: 'horizon', name: 'Horizon', sub: 'Plans + milestones', type: 'branch', status: 'ACTIVE', expanded: true,
      children: [
        {
          id: 'challenge', name: '90-Day\nChallenge', sub: 'Apr 10 - Jul 9 2026', type: 'division', status: 'ACTIVE', expanded: true,
          children: [
            { id: 'c-current', name: 'Day 25/90', sub: 'Current', type: 'leaf', status: 'ACTIVE', expanded: false, children: [] },
            { id: 'c-p1', name: 'Phase 1', sub: 'Day 1-30: foundation + first $', type: 'leaf', status: 'ACTIVE', expanded: false, children: [] },
            { id: 'c-p2', name: 'Phase 2', sub: 'Day 31-60: scale + content', type: 'leaf', status: 'PLANNED', expanded: false, children: [] },
            { id: 'c-p3', name: 'Phase 3', sub: 'Day 61-90: products + customers', type: 'leaf', status: 'PLANNED', expanded: false, children: [] },
          ],
        },
        {
          id: 'short', name: 'Short-Term', sub: 'Next 30 days', type: 'division', status: 'ACTIVE', expanded: false,
          children: [
            { id: 's-veygo', name: 'Veygo\nPayout', sub: 'Critical, ~80% confidence', type: 'leaf', status: 'ACTIVE', expanded: false, children: [] },
            { id: 's-luno', name: 'Luno\nPoC + Ship', sub: 'Tier-1 candidate', type: 'leaf', status: 'BUILDING', expanded: false, children: [] },
            { id: 's-bip', name: '5+5 BIP\nArc', sub: 'Pejji 1-5 / Securva 6-10', type: 'leaf', status: 'BUILDING', expanded: false, children: [] },
            { id: 's-customers', name: '10 Securva\nCustomers', sub: 'First-international SA, Pakstoor', type: 'leaf', status: 'ACTIVE', expanded: false, children: [] },
          ],
        },
        {
          id: 'medium', name: 'Medium-Term', sub: 'Next 90 days', type: 'division', status: 'PLANNED', expanded: false,
          children: [
            { id: 'm-bounty', name: '$15-40K\nBounty', sub: 'Cumulative target', type: 'leaf', status: 'PLANNED', expanded: false, children: [] },
            { id: 'm-securvav2', name: 'Securva v2', sub: 'Continuous audit', type: 'leaf', status: 'PLANNED', expanded: false, children: [] },
            { id: 'm-vendor', name: 'Vendor Sweep\nMonetize', sub: 'Standalone product', type: 'leaf', status: 'PLANNED', expanded: false, children: [] },
          ],
        },
        {
          id: 'long', name: 'Long-Term', sub: '1-3 years', type: 'division', status: 'FUTURE', expanded: false,
          children: [
            { id: 'l-100', name: '100+\nCustomers', sub: 'Securva at scale', type: 'leaf', status: 'FUTURE', expanded: false, children: [] },
            { id: 'l-firm', name: 'Securva\nFirm', sub: 'Web2 audit shop', type: 'leaf', status: 'FUTURE', expanded: false, children: [] },
            { id: 'l-foundation', name: 'BlessedOps\nFoundation', sub: 'Legacy + impact', type: 'leaf', status: 'FUTURE', expanded: false, children: [] },
          ],
        },
      ],
    },
    {
      id: 'warroom', name: 'War Room', sub: 'Wins + lessons banked', type: 'branch', status: 'ACTIVE', expanded: true,
      children: [
        {
          id: 'achievements', name: 'Achievements', sub: '90-day wins', type: 'division', status: 'ACTIVE', expanded: false,
          children: [
            { id: 'a-d1', name: 'Day 1', sub: 'Securva foundation set', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
            { id: 'a-d7', name: 'Day 7', sub: 'Flutterwave 3 keys disclosed', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
            { id: 'a-d12', name: 'Day 12', sub: 'Wave 3 launch + Vercel migration', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
            { id: 'a-d18', name: 'Day 18', sub: 'First paid YWH (Veygo)', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
            { id: 'a-d23', name: 'Day 23', sub: 'First BC accepted (Fivetran)', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
            { id: 'a-d24', name: 'Day 24', sub: 'OS migration to CF Pages', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
            { id: 'a-d25', name: 'Day 25', sub: 'Securva launch + Veygo PoC', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
            { id: 'a-sa', name: 'First Intl\nUser', sub: 'Pakstoor (SA)', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
          ],
        },
        {
          id: 'lessons', name: 'Lessons\n(Skeptic Gates)', sub: '12 HARD RULES banked', type: 'division', status: 'ACTIVE', expanded: false,
          children: [
            { id: 'l-redteamer', name: 'Redteamer\nPOV Gate', sub: 'Day 25 - Sophos N/A', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
            { id: 'l-shellfull', name: 'Shell-vs-Full\nApp Cordova', sub: 'Day 25', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
            { id: 'l-minified', name: 'Minified-Bundle\nBlind Spot', sub: 'Day 25', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
            { id: 'l-dexclass', name: 'DEX-Class\nPresence Gate', sub: 'Day 25 - DbInspector FP', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
            { id: 'l-cognito', name: 'Cognito\nUserConfirmed', sub: 'Day 25 - literal field check', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
            { id: 'l-showtell', name: 'Show-Don\'t-Tell\nSubmission', sub: 'Day 23', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
            { id: 'l-impact', name: 'PoCs Show\nImpact', sub: 'Day 18 - Veygo CVSS', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
            { id: 'l-7gate', name: 'Pre-Submit\n7-Gate', sub: 'Hermes OOS catch', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
            { id: 'l-emdash', name: 'No Em-Dashes', sub: 'HARD RULE everywhere', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
            { id: 'l-wafcors', name: 'WAF-Wrapped\nCORS = Cosmetic', sub: 'CF Access + DataDome', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
            { id: 'l-video', name: 'Video PoC\n> Setup PoC', sub: 'BlueStacks 1.5h saga', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
            { id: 'l-tg', name: 'Always Reply\non Telegram', sub: 'When pinged from TG', type: 'leaf', status: 'LIVE', expanded: false, children: [] },
          ],
        },
      ],
    },
    {
      id: 'team', name: 'Team', sub: 'Governance', type: 'branch', status: 'ACTIVE', expanded: false,
      children: [
        { id: 't-founder', name: 'Kingsley\nOlukanni', sub: 'Founder & Operator', type: 'leaf', status: 'ACTIVE', expanded: false, children: [] },
        { id: 't-buddy', name: 'Buddy AI', sub: 'Operational agent', type: 'leaf', status: 'ACTIVE', expanded: false, children: [] },
        { id: 't-coo', name: 'COO', sub: 'Future hire', type: 'leaf', status: 'OPEN', expanded: false, children: [] },
        { id: 't-sales', name: 'Sales Lead', sub: 'Future hire', type: 'leaf', status: 'OPEN', expanded: false, children: [] },
        { id: 't-eng', name: 'Engineering', sub: 'Future hire', type: 'leaf', status: 'OPEN', expanded: false, children: [] },
      ],
    },
  ],
}

// ─── Node metadata (Phase 2) ──────────────────────────────────────────────────
// Sidecar to the tree: tags + lastUsed + doc + notes per node id.
// Tags: TOOL / ORG / PLAN / ACHIEVEMENT / LESSON / MILESTONE / TEAM
const STALE_DAYS = 14
const today = new Date().toISOString().split('T')[0]
const daysAgo = n => new Date(Date.now() - n * 86400000).toISOString().split('T')[0]

const NODE_META = {
  // Ventures (ORG)
  'ventures':         { tags: ['ORG'] },
  'securva':          { tags: ['ORG'], doc: 'https://securva.net' },
  'sec-scanner':      { tags: ['ORG', 'TOOL'], lastUsed: daysAgo(0), doc: 'https://securva.net' },
  'sec-vendor':       { tags: ['ORG', 'TOOL'], lastUsed: daysAgo(2), doc: '/vendor' },
  'sec-research':     { tags: ['ORG', 'TOOL'], lastUsed: daysAgo(3), doc: '/research' },
  'sec-tin':          { tags: ['ORG', 'TOOL'], lastUsed: daysAgo(5) },
  'sec-disclosures':  { tags: ['ORG'], lastUsed: daysAgo(1) },
  'sec-v2':           { tags: ['ORG', 'PLAN'] },
  'pejji':            { tags: ['ORG'], doc: 'https://pejji.com' },
  'pejji-com':        { tags: ['ORG'], lastUsed: daysAgo(2), doc: 'https://pejji.com' },
  'pejji-ng':         { tags: ['ORG'], lastUsed: daysAgo(2), doc: 'https://pejji.ng' },
  'pejji-quiz':       { tags: ['ORG', 'TOOL'], lastUsed: daysAgo(7) },
  'pejji-outreach':   { tags: ['ORG'], lastUsed: daysAgo(3) },
  'bounty':           { tags: ['ORG'] },
  'b-ywh':            { tags: ['ORG'], lastUsed: daysAgo(1), notes: 'Veygo Critical closed Dup 2026-05-05. Velocity-saturated pattern banked.' },
  'b-bc':             { tags: ['ORG'], lastUsed: daysAgo(1), notes: 'Auth0 client_secret Triaged P2 2026-05-05, customer review pending.' },
  'b-immunefi':       { tags: ['ORG'], lastUsed: daysAgo(7), notes: 'Aster pending. Veda + Vesu queued.' },
  'b-h1':             { tags: ['ORG'], lastUsed: daysAgo(5) },
  'media':            { tags: ['ORG'] },
  'm-bip':            { tags: ['ORG', 'PLAN'], lastUsed: daysAgo(0) },
  'm-x':              { tags: ['ORG', 'TOOL'], lastUsed: daysAgo(0) },
  'm-li':             { tags: ['ORG', 'TOOL'], lastUsed: daysAgo(2) },
  'm-yt':             { tags: ['ORG', 'PLAN'] },
  'future-ventures':  { tags: ['ORG', 'PLAN'] },
  'fv-rentals':       { tags: ['ORG', 'PLAN'] },
  'fv-armor':         { tags: ['ORG', 'PLAN'] },
  'fv-vault':         { tags: ['ORG', 'PLAN'] },
  'fv-investments':   { tags: ['ORG', 'PLAN'] },

  // Arsenal (TOOL)
  'arsenal':          { tags: ['TOOL'] },
  'recon':            { tags: ['TOOL'] },
  'r-net':            { tags: ['TOOL'], lastUsed: daysAgo(0), notes: 'amass / subfinder / asnmap / mapcidr / httpx / nuclei. Day 24 ASN-pivot build banked.' },
  'r-web':            { tags: ['TOOL'], lastUsed: daysAgo(0), notes: 'Caido proxy + dork-runner v4.9 + cognito-scanner.' },
  'r-mobile':         { tags: ['TOOL'], lastUsed: daysAgo(0), notes: 'jadx / apkleaks / Frida / Objection / gplaydl / Genymotion (trial pending). Used heavily Day 25.' },
  'r-cloud':          { tags: ['TOOL'], lastUsed: daysAgo(1), notes: 's3scanner / Azure Blob probe / Shodan / Censys. Day 24 Sophos-testing bucket discovery.' },
  'r-jwt':            { tags: ['TOOL'], lastUsed: daysAgo(2), notes: 'jwt_tool integrated, validation deferred (need sandbox JWT capture).' },
  'methodology':      { tags: ['TOOL'] },
  'mt-patternlab':    { tags: ['TOOL'], lastUsed: daysAgo(0), notes: '61 entries banked. Pattern-yield calibration: SSRF saturated, mature SDKs 0%, slow-lane works.' },
  'mt-skeptic':       { tags: ['TOOL'], lastUsed: daysAgo(0), notes: '12 HARD RULES banked. Each gate prevents an FP class.' },
  'mt-hsr':           { tags: ['TOOL'], lastUsed: daysAgo(2), notes: 'Hunter -> Skeptic -> Referee adversarial pattern. Catches FPs at Hunter -> Skeptic transition.' },
  'mt-mobile':        { tags: ['TOOL'], lastUsed: daysAgo(0), notes: '10-section v1 doc. Cordova/hybrid CSPT methodology - publishable Securva content.' },
  'mt-redteamer':     { tags: ['TOOL'], lastUsed: daysAgo(0), notes: 'HARD RULE banked Day 25. Working PoC + real-victim demo + attacker-POV walkthrough required.' },
  'pipelines':        { tags: ['TOOL'] },
  'p-news':           { tags: ['TOOL'], lastUsed: daysAgo(0) },
  'p-snapshot':       { tags: ['TOOL'], lastUsed: daysAgo(0) },
  'p-web3':           { tags: ['TOOL'], lastUsed: daysAgo(0) },
  'p-phaseintel':     { tags: ['TOOL'], lastUsed: daysAgo(1) },
  'p-monitors':       { tags: ['TOOL'], lastUsed: daysAgo(0) },
  'p-tg':             { tags: ['TOOL'], lastUsed: daysAgo(0) },
  'knowledge':        { tags: ['TOOL'] },
  'k-memory':         { tags: ['TOOL'], lastUsed: daysAgo(0) },
  'k-scope':          { tags: ['TOOL'], lastUsed: daysAgo(1) },
  'k-brand':          { tags: ['TOOL'], lastUsed: daysAgo(7) },
  'k-notion':         { tags: ['TOOL'], lastUsed: daysAgo(0) },
  'k-frida':          { tags: ['TOOL'], lastUsed: daysAgo(1) },

  // Horizon (PLAN + MILESTONE for 90-day)
  'horizon':          { tags: ['PLAN'] },
  'challenge':        { tags: ['PLAN', 'MILESTONE'] },
  'c-current':        { tags: ['PLAN', 'MILESTONE'] },
  'c-p1':             { tags: ['PLAN', 'MILESTONE'] },
  'c-p2':             { tags: ['PLAN', 'MILESTONE'] },
  'c-p3':             { tags: ['PLAN', 'MILESTONE'] },
  'short':            { tags: ['PLAN'] },
  's-veygo':          { tags: ['PLAN'], notes: 'Veygo Critical closed Dup 2026-05-05. Pivot to slow-lane.' },
  's-luno':           { tags: ['PLAN'], notes: 'Tier-1 candidate. Awaiting Genymotion Cloud trial approval (admin@blessedops.com).' },
  's-bip':            { tags: ['PLAN'] },
  's-customers':      { tags: ['PLAN'] },
  'medium':           { tags: ['PLAN'] },
  'm-bounty':         { tags: ['PLAN'] },
  'm-securvav2':      { tags: ['PLAN'] },
  'm-vendor':         { tags: ['PLAN'] },
  'long':             { tags: ['PLAN'] },
  'l-100':            { tags: ['PLAN'] },
  'l-firm':           { tags: ['PLAN'] },
  'l-foundation':     { tags: ['PLAN'] },

  // War Room - Achievements
  'warroom':          { tags: ['ACHIEVEMENT', 'LESSON'] },
  'achievements':     { tags: ['ACHIEVEMENT'] },
  'a-d1':             { tags: ['ACHIEVEMENT'] },
  'a-d7':             { tags: ['ACHIEVEMENT'] },
  'a-d12':            { tags: ['ACHIEVEMENT'] },
  'a-d18':            { tags: ['ACHIEVEMENT'] },
  'a-d23':            { tags: ['ACHIEVEMENT'] },
  'a-d24':            { tags: ['ACHIEVEMENT'] },
  'a-d25':            { tags: ['ACHIEVEMENT'] },
  'a-sa':             { tags: ['ACHIEVEMENT'] },

  // Lessons
  'lessons':          { tags: ['LESSON'] },
  'l-redteamer':      { tags: ['LESSON'] },
  'l-shellfull':      { tags: ['LESSON'] },
  'l-minified':       { tags: ['LESSON'] },
  'l-dexclass':       { tags: ['LESSON'] },
  'l-cognito':        { tags: ['LESSON'] },
  'l-showtell':       { tags: ['LESSON'] },
  'l-impact':         { tags: ['LESSON'] },
  'l-7gate':          { tags: ['LESSON'] },
  'l-emdash':         { tags: ['LESSON'] },
  'l-wafcors':        { tags: ['LESSON'] },
  'l-video':          { tags: ['LESSON'] },
  'l-tg':             { tags: ['LESSON'] },

  // Team
  'team':             { tags: ['TEAM'] },
  't-founder':        { tags: ['TEAM'], notes: 'Kingsley Olukanni - Founder & Operator. All roles for now.' },
  't-buddy':          { tags: ['TEAM'], lastUsed: daysAgo(0), notes: 'Buddy AI - operational hunting agent on box.' },
  't-coo':            { tags: ['TEAM', 'PLAN'] },
  't-sales':          { tags: ['TEAM', 'PLAN'] },
  't-eng':            { tags: ['TEAM', 'PLAN'] },
}

// Cross-links - dotted lines connecting nodes across branches.
// Captures "how the dots connect": tool used in achievement, achievement produced lesson, etc.
const CROSS_LINKS = [
  // Methodology -> Lessons (the gates that produced the rules)
  { from: 'mt-redteamer', to: 'l-redteamer', label: 'produced' },
  { from: 'mt-skeptic', to: 'l-dexclass', label: 'caught' },
  { from: 'mt-skeptic', to: 'l-minified', label: 'caught' },
  { from: 'mt-skeptic', to: 'l-shellfull', label: 'caught' },
  { from: 'mt-skeptic', to: 'l-wafcors', label: 'caught' },
  { from: 'mt-skeptic', to: 'l-cognito', label: 'caught' },
  { from: 'mt-mobile', to: 'l-minified', label: 'documented' },
  { from: 'mt-mobile', to: 'l-shellfull', label: 'documented' },
  { from: 'mt-mobile', to: 'l-video', label: 'documented' },

  // Tools -> Achievements (the tools that delivered the wins)
  { from: 'r-mobile', to: 'a-d25', label: 'powered' },
  { from: 'r-web', to: 'a-d18', label: 'powered' },
  { from: 'r-cloud', to: 'a-d24', label: 'powered' },
  { from: 'mt-patternlab', to: 'a-d18', label: 'powered' },
  { from: 'mt-redteamer', to: 'a-d25', label: 'powered' },

  // Bounty programs -> Achievements
  { from: 'b-ywh', to: 'a-d18', label: 'first paid' },
  { from: 'b-bc', to: 'a-d23', label: 'first accepted' },

  // Securva products -> Achievements
  { from: 'sec-scanner', to: 'a-d12', label: 'launched' },
  { from: 'sec-scanner', to: 'a-sa', label: 'reached' },

  // Plans -> Tools (what enables what)
  { from: 's-luno', to: 'r-mobile', label: 'needs' },
  { from: 'm-securvav2', to: 'mt-mobile', label: 'leverages' },
  { from: 'm-vendor', to: 'sec-vendor', label: 'productizes' },
]

// ─── Tree utilities ───────────────────────────────────────────────────────────
function countLeaves(node) {
  if (!node.children || node.children.length === 0) return 1
  return node.children.reduce((s, c) => s + countLeaves(c), 0)
}

function computeDefaultPositions(tree) {
  const positions = {}
  const V_GAP = 200
  function layout(node, depth, xStart, xEnd) {
    const dim = DIM[node.type] || DIM.leaf
    positions[node.id] = { x: (xStart + xEnd) / 2 - dim.w / 2, y: 80 + depth * V_GAP }
    if (!node.children || node.children.length === 0) return
    const total = node.children.reduce((s, c) => s + countLeaves(c), 0)
    let cx = xStart
    node.children.forEach(child => {
      const w = (countLeaves(child) / total) * (xEnd - xStart)
      layout(child, depth + 1, cx, cx + w)
      cx += w
    })
  }
  layout(tree, 0, 100, 5800)
  return positions
}

function flatNodes(node, acc = []) {
  acc.push(node)
  if (node.children) node.children.forEach(c => flatNodes(c, acc))
  return acc
}

function findNode(node, id) {
  if (node.id === id) return node
  if (!node.children) return null
  for (const c of node.children) { const f = findNode(c, id); if (f) return f }
  return null
}

function getConnections(node, acc = []) {
  if (!node.children) return acc
  node.children.forEach(child => { acc.push({ parentId: node.id, childId: child.id }); getConnections(child, acc) })
  return acc
}

function deepClone(o) { return JSON.parse(JSON.stringify(o)) }

function addNodeToTree(tree, parentId, newNode) {
  const t = deepClone(tree)
  const p = findNode(t, parentId)
  if (p) { if (!p.children) p.children = []; p.children.push(newNode) }
  return t
}

function editNodeInTree(tree, id, changes) {
  const t = deepClone(tree)
  const n = findNode(t, id)
  if (n) Object.assign(n, changes)
  return t
}

function deleteNodeFromTree(tree, id) {
  const t = deepClone(tree)
  function rm(node) {
    if (!node.children) return
    node.children = node.children.filter(c => c.id !== id)
    node.children.forEach(rm)
  }
  rm(t)
  return t
}

// ─── Modal shared styles ──────────────────────────────────────────────────────
const mOverlay = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500, padding: 20,
}
const mBox = {
  background: '#13151A', border: '1px solid #2A2E38', borderRadius: 14,
  padding: '24px 24px 20px', width: '100%', maxWidth: 360,
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
}
const mInput = {
  width: '100%', background: '#1A1D23', border: '1px solid #2A2E38', borderRadius: 8,
  color: '#E8E9EC', padding: '10px 12px', fontSize: 14, outline: 'none',
  boxSizing: 'border-box', fontFamily: 'inherit', marginBottom: 12,
}
const mLabel = { fontSize: 11, color: '#6B7080', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 5, display: 'block' }
const mRow = { display: 'flex', gap: 8, marginTop: 16 }
const mBtn = (primary, danger) => ({
  flex: 1, padding: '10px', borderRadius: 8, border: danger ? '1px solid rgba(255,107,107,0.3)' : 'none',
  cursor: 'pointer', fontSize: 14, fontWeight: 600,
  background: primary ? 'linear-gradient(135deg, #00E5A0, #7C8CF8)' : danger ? 'rgba(255,107,107,0.12)' : 'rgba(255,255,255,0.07)',
  color: primary ? '#0B0D11' : danger ? '#FF6B6B' : '#C8CAD0',
})

// ─── Modals ───────────────────────────────────────────────────────────────────
function AddModal({ parentNode, onAdd, onClose }) {
  const [name, setName] = useState('')
  const [sub, setSub] = useState('')
  const [status, setStatus] = useState('PLANNED')
  const type = parentNode.type === 'root' ? 'branch' : parentNode.type === 'branch' ? 'division' : 'leaf'
  const submit = () => {
    if (!name.trim()) return
    onAdd({ id: 'node_' + Date.now(), name: name.trim(), sub: sub.trim(), type, status, expanded: false, children: [] })
  }
  return (
    <div style={mOverlay} onPointerDown={e => e.target === e.currentTarget && onClose()}>
      <div style={mBox}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#E8E9EC', marginBottom: 18 }}>
          Add to {parentNode.name.replace('\n', ' ')}
        </div>
        <label style={mLabel}>Name</label>
        <input style={mInput} value={name} onChange={e => setName(e.target.value)} placeholder="Node name" autoFocus />
        <label style={mLabel}>Subtitle</label>
        <input style={mInput} value={sub} onChange={e => setSub(e.target.value)} placeholder="Short description" />
        <label style={mLabel}>Status</label>
        <select style={{ ...mInput, marginBottom: 0 }} value={status} onChange={e => setStatus(e.target.value)}>
          {Object.keys(STATUS).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <div style={mRow}>
          <button style={mBtn(false)} onClick={onClose}>Cancel</button>
          <button style={mBtn(true)} onClick={submit}>Add Node</button>
        </div>
      </div>
    </div>
  )
}

function EditModal({ node, onSave, onClose }) {
  const [name, setName] = useState(node.name)
  const [sub, setSub] = useState(node.sub || '')
  const [status, setStatus] = useState(node.status)
  return (
    <div style={mOverlay} onPointerDown={e => e.target === e.currentTarget && onClose()}>
      <div style={mBox}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#E8E9EC', marginBottom: 18 }}>Edit Node</div>
        <label style={mLabel}>Name</label>
        <input style={mInput} value={name} onChange={e => setName(e.target.value)} autoFocus />
        <label style={mLabel}>Subtitle</label>
        <input style={mInput} value={sub} onChange={e => setSub(e.target.value)} />
        <label style={mLabel}>Status</label>
        <select style={{ ...mInput, marginBottom: 0 }} value={status} onChange={e => setStatus(e.target.value)}>
          {Object.keys(STATUS).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <div style={mRow}>
          <button style={mBtn(false)} onClick={onClose}>Cancel</button>
          <button style={mBtn(true)} onClick={() => onSave({ name, sub, status })}>Save</button>
        </div>
      </div>
    </div>
  )
}

function DeleteModal({ node, onConfirm, onClose }) {
  return (
    <div style={mOverlay} onPointerDown={e => e.target === e.currentTarget && onClose()}>
      <div style={mBox}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#E8E9EC', marginBottom: 12 }}>Delete Node?</div>
        <div style={{ fontSize: 14, color: '#8A8F98', marginBottom: 20, lineHeight: 1.6 }}>
          Remove <strong style={{ color: '#E8E9EC' }}>{node.name.replace('\n', ' ')}</strong> and all its children? This cannot be undone.
        </div>
        <div style={mRow}>
          <button style={mBtn(false)} onClick={onClose}>Cancel</button>
          <button style={mBtn(false, true)} onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  )
}

// ─── Time helpers ─────────────────────────────────────────────────────────────
function formatRelativeTime(iso) {
  if (!iso) return ''
  const t = new Date(iso).getTime()
  if (isNaN(t)) return ''
  const sec = Math.max(0, Math.floor((Date.now() - t) / 1000))
  if (sec < 60) return 'just now'
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`
  return `${Math.floor(sec / 86400)}d ago`
}

// ─── Staleness helper ─────────────────────────────────────────────────────────
function isStale(meta, remoteLastUsed) {
  if (!meta?.tags?.includes('TOOL')) return false
  const lu = remoteLastUsed || meta.lastUsed
  if (!lu) return true
  const days = (Date.now() - new Date(lu + 'T00:00:00').getTime()) / 86400000
  return days > STALE_DAYS
}

// ─── MapNode ──────────────────────────────────────────────────────────────────
function MapNode({ node, pos, meta, remoteLastUsed, dimmed, selected, onDragStart, onClick, onEdit, onDelete, onAdd, onToggle }) {
  const [hovered, setHovered] = useState(false)
  const dim = DIM[node.type] || DIM.leaf
  const statusColor = STATUS[node.status] || '#3A3E4A'
  const hasChildren = node.children && node.children.length > 0
  const isRoot = node.type === 'root'
  const isBranch = node.type === 'branch'
  const stale = isStale(meta, remoteLastUsed)
  const downRef = useRef(null)

  return (
    <div
      style={{
        position: 'absolute', left: pos.x, top: pos.y, width: dim.w, height: dim.h,
        borderRadius: isRoot ? 16 : isBranch ? 14 : 10,
        cursor: 'grab', userSelect: 'none', touchAction: 'none',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: '8px 10px', boxSizing: 'border-box',
        opacity: dimmed ? 0.25 : 1,
        transition: 'opacity 0.15s, box-shadow 0.15s',
        ...(isRoot ? {
          background: 'radial-gradient(ellipse at 40% 30%, #7c1d1d 0%, #3d0e0e 55%, #1a0606 100%)',
          border: selected ? '2px solid #00E5A0' : '1.5px solid #991b1b',
          boxShadow: selected ? '0 0 32px rgba(0,229,160,0.45)' : hovered ? '0 0 28px rgba(153,27,27,0.55)' : '0 0 16px rgba(153,27,27,0.3)',
        } : isBranch ? {
          background: 'linear-gradient(135deg, #1c1a10 0%, #0e0c06 100%)',
          border: selected ? '2px solid #00E5A0' : '1.5px solid #78611a',
          boxShadow: selected ? '0 0 22px rgba(0,229,160,0.4)' : hovered ? '0 0 18px rgba(212,168,85,0.25)' : '0 0 8px rgba(212,168,85,0.1)',
        } : {
          background: '#111318',
          border: stale ? '1px solid rgba(255,107,107,0.55)' : selected ? '1.5px solid #00E5A0' : `1px solid ${hovered ? '#2A2E38' : '#1C1E25'}`,
          boxShadow: stale ? '0 0 12px rgba(255,107,107,0.35)' : selected ? '0 0 14px rgba(0,229,160,0.4)' : hovered ? '0 4px 14px rgba(0,0,0,0.5)' : 'none',
        }),
      }}
      onPointerDown={e => {
        e.stopPropagation()
        downRef.current = { x: e.clientX, y: e.clientY, t: Date.now() }
        onDragStart(e, node.id)
      }}
      onPointerUp={e => {
        e.stopPropagation()
        const d = downRef.current
        if (!d) return
        const dx = Math.abs(e.clientX - d.x)
        const dy = Math.abs(e.clientY - d.y)
        const dt = Date.now() - d.t
        if (dx < 5 && dy < 5 && dt < 350) onClick(node.id)
        downRef.current = null
      }}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      <div style={{ position: 'absolute', top: 7, right: 8, width: 6, height: 6, borderRadius: '50%', background: statusColor }} />
      {stale && <div title="Tool unused 14+ days" style={{ position: 'absolute', top: 6, left: 8, fontSize: 10, color: '#FF6B6B' }}>⚠</div>}

      {node.name.split('\n').map((line, i) => (
        <div key={i} style={{
          fontSize: isRoot ? 12 : isBranch ? 11 : 10,
          fontWeight: isRoot ? 700 : 600,
          color: isRoot ? '#f5c6c6' : isBranch ? '#e9d77a' : '#C4C6CE',
          letterSpacing: isRoot ? 1.5 : 0.4,
          textAlign: 'center', lineHeight: 1.3,
          fontFamily: isRoot
            ? "'Cormorant Garamond', Georgia, serif"
            : isBranch ? "'Instrument Sans', 'Inter', sans-serif"
            : "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          textTransform: isRoot ? 'uppercase' : 'none',
        }}>{line}</div>
      ))}

      {node.sub && (
        <div style={{ fontSize: 9, color: '#4A4E58', marginTop: 3, textAlign: 'center', lineHeight: 1.2, fontFamily: "'JetBrains Mono', monospace" }}>
          {node.sub}
        </div>
      )}

      {hovered && (
        <div style={{ position: 'absolute', top: -24, right: 0, display: 'flex', gap: 3, zIndex: 10 }}>
          {node.type !== 'leaf' && (
            <button
              onPointerDown={e => { e.stopPropagation(); onAdd(node.id) }}
              style={{ background: '#1A1D23', border: '1px solid #2A2E38', borderRadius: 4, color: '#00E5A0', fontSize: 12, padding: '2px 7px', cursor: 'pointer', lineHeight: 1.4 }}
            >+</button>
          )}
          <button
            onPointerDown={e => { e.stopPropagation(); onEdit(node.id) }}
            style={{ background: '#1A1D23', border: '1px solid #2A2E38', borderRadius: 4, color: '#7C8CF8', fontSize: 11, padding: '2px 6px', cursor: 'pointer', lineHeight: 1.4 }}
          >✎</button>
          {node.type !== 'root' && (
            <button
              onPointerDown={e => { e.stopPropagation(); onDelete(node.id) }}
              style={{ background: '#1A1D23', border: '1px solid #2A2E38', borderRadius: 4, color: '#FF6B6B', fontSize: 11, padding: '2px 6px', cursor: 'pointer', lineHeight: 1.4 }}
            >✕</button>
          )}
        </div>
      )}

      {hasChildren && (
        <button
          onPointerDown={e => { e.stopPropagation(); onToggle(node.id) }}
          style={{
            position: 'absolute', bottom: -11, left: '50%', transform: 'translateX(-50%)',
            width: 20, height: 20, borderRadius: '50%',
            background: '#1A1D23', border: '1px solid #2A2E38', color: '#6B7080',
            fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            lineHeight: 1, zIndex: 5,
          }}
        >{node.expanded ? '−' : '+'}</button>
      )}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────
export function BlessedOpsMindMapDraggable() {
  const [tree, setTree] = useState(DEFAULT_TREE)
  const [positions, setPositions] = useState(() => computeDefaultPositions(DEFAULT_TREE))
  const [mmLoaded, setMmLoaded] = useState(false)
  const [transform, setTransform] = useState({ x: 0, y: 0, scale: 0.6 })
  const [modal, setModal] = useState(null)
  const [tagFilter, setTagFilter] = useState(null)
  const [selectedId, setSelectedId] = useState(null)
  const [showCrossLinks, setShowCrossLinks] = useState(true)
  const [paOpen, setPaOpen] = useState(false)
  const [paBrief, setPaBrief] = useState(null)
  const [paBriefLoading, setPaBriefLoading] = useState(false)
  const [paChat, setPaChat] = useState([])
  const [paInput, setPaInput] = useState('')
  const [paChatLoading, setPaChatLoading] = useState(false)
  const [osStateRemote, setOsStateRemote] = useState(null)
  const [osStateLoading, setOsStateLoading] = useState(false)

  const containerRef = useRef(null)
  const transformRef = useRef(transform)
  const positionsRef = useRef(positions)
  const treeRef = useRef(tree)
  const dragRef = useRef(null)
  const panRef = useRef(null)
  const saveTimerRef = useRef(null)
  const isDraggingRef = useRef(false)

  useEffect(() => { transformRef.current = transform }, [transform])
  useEffect(() => { positionsRef.current = positions }, [positions])
  useEffect(() => { treeRef.current = tree }, [tree])

  useEffect(() => {
    mmLoad().then(stored => {
      if (stored?.tree) setTree(stored.tree)
      if (stored?.positions) setPositions(stored.positions)
      setMmLoaded(true)
    })
  }, [])

  // Debounced save - never fires during active drag, only after settle
  useEffect(() => {
    if (!mmLoaded) return
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => {
      if (!isDraggingRef.current) {
        mmSave({ tree: treeRef.current, positions: positionsRef.current })
      }
    }, SAVE_DEBOUNCE_MS)
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current) }
  }, [tree, positions, mmLoaded])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const { width, height } = el.getBoundingClientRect()
    setTransform({ x: width / 2 - 2900 * 0.6, y: height * 0.04, scale: 0.6 })
  }, [])

  // Auto-sync: fetch latest /api/os-state on mount + on demand
  const fetchOsState = useCallback(async () => {
    setOsStateLoading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) { setOsStateLoading(false); return }
      const r = await fetch('/api/os-state', { headers: { 'Authorization': `Bearer ${session.access_token}` } })
      if (!r.ok) { setOsStateLoading(false); return }
      const j = await r.json()
      if (j?.state) setOsStateRemote(j.state)
    } catch {}
    setOsStateLoading(false)
  }, [])

  useEffect(() => { fetchOsState() }, [fetchOsState])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const onWheel = e => {
      e.preventDefault()
      const rect = el.getBoundingClientRect()
      const mx = e.clientX - rect.left
      const my = e.clientY - rect.top
      const factor = e.deltaY > 0 ? 0.9 : 1.1
      setTransform(prev => {
        const newScale = Math.max(0.1, Math.min(3, prev.scale * factor))
        const r = newScale / prev.scale
        return { scale: newScale, x: mx - r * (mx - prev.x), y: my - r * (my - prev.y) }
      })
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [])

  // Window-level pointer handlers - bulletproof against canvas occlusion
  useEffect(() => {
    const onMove = e => {
      if (dragRef.current) {
        const t = transformRef.current
        const { nodeId, ox, oy } = dragRef.current
        const wx = (e.clientX - t.x) / t.scale
        const wy = (e.clientY - t.y) / t.scale
        setPositions(prev => ({ ...prev, [nodeId]: { x: wx - ox, y: wy - oy } }))
      } else if (panRef.current) {
        const dx = e.clientX - panRef.current.startX
        const dy = e.clientY - panRef.current.startY
        setTransform(prev => ({ ...prev, x: panRef.current.tx + dx, y: panRef.current.ty + dy }))
      }
    }
    const onUp = () => {
      dragRef.current = null
      panRef.current = null
      isDraggingRef.current = false
      // Trigger a save now that drag is complete
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
      saveTimerRef.current = setTimeout(() => {
        mmSave({ tree: treeRef.current, positions: positionsRef.current })
      }, SAVE_DEBOUNCE_MS)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [])

  const handleCanvasPointerDown = e => {
    if (e.target !== e.currentTarget) return
    panRef.current = { startX: e.clientX, startY: e.clientY, tx: transformRef.current.x, ty: transformRef.current.y }
  }

  const handleNodeDragStart = useCallback((e, nodeId) => {
    const t = transformRef.current
    const wx = (e.clientX - t.x) / t.scale
    const wy = (e.clientY - t.y) / t.scale
    const pos = positionsRef.current[nodeId] || { x: 0, y: 0 }
    dragRef.current = { nodeId, ox: wx - pos.x, oy: wy - pos.y }
    isDraggingRef.current = true
  }, [])

  const handleToggle = useCallback(id => {
    setTree(prev => editNodeInTree(prev, id, { expanded: !findNode(prev, id)?.expanded }))
  }, [])

  const handleAdd = useCallback(parentId => setModal({ type: 'add', nodeId: parentId }), [])
  const handleEdit = useCallback(id => setModal({ type: 'edit', nodeId: id }), [])
  const handleDelete = useCallback(id => setModal({ type: 'delete', nodeId: id }), [])

  const confirmAdd = newNode => {
    const parentPos = positions[modal.nodeId] || { x: 400, y: 400 }
    const newTree = addNodeToTree(tree, modal.nodeId, newNode)
    setTree(newTree)
    setPositions(prev => ({ ...prev, [newNode.id]: { x: parentPos.x + 20, y: parentPos.y + 200 } }))
    setModal(null)
  }

  const confirmEdit = changes => { setTree(editNodeInTree(tree, modal.nodeId, changes)); setModal(null) }

  const confirmDelete = () => {
    const newPos = { ...positions }
    const removePos = node => { delete newPos[node.id]; node.children?.forEach(removePos) }
    const target = findNode(tree, modal.nodeId)
    if (target) removePos(target)
    setTree(deleteNodeFromTree(tree, modal.nodeId))
    setPositions(newPos)
    setModal(null)
  }

  const resetLayout = () => setPositions(computeDefaultPositions(tree))

  const fitToView = () => {
    const el = containerRef.current
    if (!el) return
    const visIds = new Set()
    const collect = node => {
      visIds.add(node.id)
      if (node.expanded && node.children) node.children.forEach(collect)
    }
    collect(tree)
    const visPos = Object.entries(positions).filter(([id]) => visIds.has(id))
    if (visPos.length === 0) return
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
    for (const [id, p] of visPos) {
      const dim = DIM[(findNode(tree, id) || { type: 'leaf' }).type] || DIM.leaf
      minX = Math.min(minX, p.x)
      minY = Math.min(minY, p.y)
      maxX = Math.max(maxX, p.x + dim.w)
      maxY = Math.max(maxY, p.y + dim.h)
    }
    const { width, height } = el.getBoundingClientRect()
    const pad = 60
    const w = maxX - minX + pad * 2
    const h = maxY - minY + pad * 2
    const scale = Math.min(width / w, height / h, 2)
    const x = (width - (maxX + minX) * scale) / 2
    const y = (height - (maxY + minY) * scale) / 2
    setTransform({ x, y, scale })
  }

  const setAllExpanded = useCallback((expanded) => {
    setTree(prev => {
      const t = deepClone(prev)
      const walk = node => { if (node.children && node.children.length > 0) { node.expanded = expanded; node.children.forEach(walk) } }
      walk(t)
      return t
    })
    if (expanded) {
      // After expand-all, recompute positions so newly-visible nodes have positions
      setTimeout(() => {
        setPositions(prev => {
          const fresh = computeDefaultPositions(treeRef.current)
          // Keep existing dragged positions, fill in missing
          const merged = { ...fresh, ...prev }
          for (const k of Object.keys(fresh)) if (!prev[k]) merged[k] = fresh[k]
          return merged
        })
      }, 50)
    }
  }, [])

  const resetToDefault = () => {
    if (!window.confirm('Reset to default structure? All custom changes will be lost.')) return
    setTree(DEFAULT_TREE)
    setPositions(computeDefaultPositions(DEFAULT_TREE))
  }

  // ─── PA mode helpers (Phase 3) ──────────────────────────────────────────────
  const buildPAContext = useCallback(() => {
    const all = flatNodes(treeRef.current)
    const lines = []
    const groupBy = tag => all.filter(n => NODE_META[n.id]?.tags?.includes(tag))
    const status = n => `[${n.status}]`
    const remoteLastUsed = (id) => osStateRemote?.toolLastUsed?.[id]
    const fmt = n => `${n.name.replace('\n', ' ')} ${status(n)}${n.sub ? ' - ' + n.sub : ''}${NODE_META[n.id]?.notes ? ' :: ' + NODE_META[n.id].notes : ''}`

    lines.push('# BlessedOps OS State')
    if (osStateRemote) {
      lines.push(`Auto-synced ${osStateRemote.updatedAt ? `at ${osStateRemote.updatedAt}` : 'recently'}.`)
      if (osStateRemote.dayOfChallenge) lines.push(`Current day: ${osStateRemote.dayOfChallenge}/90`)
      if (osStateRemote.skepticGatesCount) lines.push(`Banked Skeptic gates: ${osStateRemote.skepticGatesCount}`)
      if (osStateRemote.memoryFilesCount) lines.push(`Memory files: ${osStateRemote.memoryFilesCount}`)
      if (osStateRemote.latestBuddyCycle) lines.push(`Latest Buddy cycle: ${osStateRemote.latestBuddyCycle}`)
      if (osStateRemote.latestEodLog) lines.push(`Latest EOD log: ${osStateRemote.latestEodLog}`)
      if (Array.isArray(osStateRemote.achievements) && osStateRemote.achievements.length) {
        lines.push('Recent achievements (auto-synced):')
        osStateRemote.achievements.forEach(a => lines.push(`  - ${a.name || a.id} ${a.status ? '[' + a.status + ']' : ''}${a.sub ? ' - ' + a.sub : ''}`))
      }
      if (Array.isArray(osStateRemote.lessons) && osStateRemote.lessons.length) {
        lines.push('Recent lessons banked (auto-synced):')
        osStateRemote.lessons.forEach(l => lines.push(`  - ${l.name || l.id}${l.sub ? ' - ' + l.sub : ''}`))
      }
    } else {
      lines.push('No auto-sync data yet (box cron has not posted).')
    }
    lines.push('')
    lines.push('## Ventures (revenue arms)')
    groupBy('ORG').forEach(n => lines.push(`- ${fmt(n)}`))
    lines.push('')
    lines.push('## Arsenal (tools + methodology + pipelines + knowledge)')
    groupBy('TOOL').forEach(n => {
      const m = NODE_META[n.id] || {}
      const lu = remoteLastUsed(n.id) || m.lastUsed
      lines.push(`- ${fmt(n)}${lu ? ` (last used ${lu})` : ' (NEVER USED)'}`)
    })
    lines.push('')
    lines.push('## Horizon (plans + milestones)')
    groupBy('PLAN').forEach(n => lines.push(`- ${fmt(n)}`))
    lines.push('')
    lines.push('## Achievements (90-day wins)')
    groupBy('ACHIEVEMENT').forEach(n => lines.push(`- ${fmt(n)}`))
    lines.push('')
    lines.push('## Lessons banked (HARD RULES)')
    groupBy('LESSON').forEach(n => lines.push(`- ${fmt(n)}`))
    lines.push('')
    lines.push('## Cross-connections (how dots connect)')
    CROSS_LINKS.forEach(l => {
      const f = all.find(n => n.id === l.from), t = all.find(n => n.id === l.to)
      if (f && t) lines.push(`- ${f.name.replace('\n', ' ')} -[${l.label}]-> ${t.name.replace('\n', ' ')}`)
    })
    return lines.join('\n')
  }, [osStateRemote])

  const PA_SYSTEM_PROMPT = `You are the BlessedOps OS Personal Assistant. You serve Kingsley Olukanni, founder of BlessedOps Group (Securva / Pejji / Bug Bounty Practice / Media). He is running a 90-day bug-bounty + Securva launch challenge (Apr 10 - Jul 9, 2026). Today is Day 26 of 90.

You have full visibility into the operator's tree state (passed in user message): Ventures / Arsenal / Horizon / Achievements / Lessons / cross-connections.

Your job:
- Answer "what's urgent / coming / stale" briefly and concretely
- Suggest next moves grounded in the tree state, not generic advice
- Surface insights from the cross-connection graph
- Be terse. Ground everything in tree data. Reference specific nodes by name.

You ALSO have WRITE access to the tree. When the user expresses intent to modify a node (update status / change name / add note / add child / delete node / mark complete), you MUST emit an action block at the very end of your response in this format:

<action>{"action":"update_node","id":"<node_id>","changes":{"status":"LIVE","sub":"new subtitle"}}</action>

Or for adding:
<action>{"action":"add_node","parentId":"<parent_id>","node":{"name":"Node Name","sub":"description","status":"ACTIVE"}}</action>

Or for deleting:
<action>{"action":"delete_node","id":"<node_id>"}</action>

Action rules:
- Use exact node ids from the tree state (e.g. "b-bc", "s-luno", "a-d25"). NEVER invent ids.
- For add_node: pick the parentId most semantically appropriate. The new node's id is auto-generated by the system.
- Status values: ACTIVE / LIVE / BUILDING / PLANNED / FUTURE / OPEN
- Only emit ONE action per response. If multiple updates are requested, do the most important first and tell the user "and one more change pending - say apply to continue."
- ALWAYS narrate the proposed change in plain language BEFORE the action block. The user must understand what's about to happen.
- Do NOT emit an action block when the user is just asking questions or requesting a brief.

Banked rules you must respect:
- Be FIRST or skip quick-win patterns (Veygo dup lesson, Day 26)
- REDTEAMER POV gate on submissions (working PoC + real victim data + attacker walkthrough)
- Show-don't-tell submission body format (command -> response -> facts)
- 70/30 web3/web2 split on hunting cadence
- No external sends without explicit approval
- No em-dashes. Use hyphens or rephrase.

When asked for "today's brief" or similar, structure as:
URGENT | COMING UP | STALE | NEXT MOVE
End with one concrete next action. (No action block for briefs.)`

  const parseActionBlock = useCallback((text) => {
    const m = text.match(/<action>([\s\S]+?)<\/action>/)
    if (!m) return null
    try {
      const parsed = JSON.parse(m[1].trim())
      if (!parsed.action) return null
      return parsed
    } catch { return null }
  }, [])

  const stripActionBlock = useCallback((text) => text.replace(/<action>[\s\S]+?<\/action>/, '').trim(), [])

  const applyPaAction = useCallback((action) => {
    if (!action) return false
    if (action.action === 'update_node' && action.id && action.changes) {
      const node = findNode(treeRef.current, action.id)
      if (!node) return false
      setTree(prev => editNodeInTree(prev, action.id, action.changes))
      return true
    }
    if (action.action === 'add_node' && action.parentId && action.node) {
      const parent = findNode(treeRef.current, action.parentId)
      if (!parent) return false
      const parentType = parent.type
      const newType = parentType === 'root' ? 'branch' : parentType === 'branch' ? 'division' : 'leaf'
      const newNode = {
        id: 'pa_' + Date.now(),
        name: action.node.name || 'New node',
        sub: action.node.sub || '',
        type: newType,
        status: action.node.status || 'PLANNED',
        expanded: false,
        children: [],
      }
      setTree(prev => addNodeToTree(prev, action.parentId, newNode))
      // Position near parent
      const parentPos = positionsRef.current[action.parentId] || { x: 400, y: 400 }
      setPositions(prev => ({ ...prev, [newNode.id]: { x: parentPos.x + 30, y: parentPos.y + 200 } }))
      return true
    }
    if (action.action === 'delete_node' && action.id) {
      const node = findNode(treeRef.current, action.id)
      if (!node || node.type === 'root') return false
      setTree(prev => deleteNodeFromTree(prev, action.id))
      return true
    }
    return false
  }, [])

  const callClaude = async (messages, maxTokens = 600) => {
    const { data: { session: s } } = await supabase.auth.getSession()
    if (!s) throw new Error('Not authenticated')
    const response = await fetch('/api/claude', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${s.access_token}` },
      body: JSON.stringify({
        model: 'claude-sonnet-4-20250514',
        max_tokens: maxTokens,
        system: PA_SYSTEM_PROMPT,
        messages,
      }),
    })
    const data = await response.json()
    return data.content?.map(c => c.text || '').join('\n') || 'No response.'
  }

  const generatePABrief = async () => {
    setPaBriefLoading(true)
    try {
      const context = buildPAContext()
      const text = await callClaude([{
        role: 'user',
        content: `Here is the current OS state:\n\n${context}\n\nGenerate today's brief. Structure: URGENT | COMING UP | STALE | NEXT MOVE. Under 200 words. End with one concrete next action.`,
      }], 500)
      setPaBrief({ text, generatedAt: new Date().toISOString() })
    } catch (e) {
      setPaBrief({ text: 'Connection error - try again. ' + (e.message || ''), generatedAt: new Date().toISOString() })
    }
    setPaBriefLoading(false)
  }

  const sendPAMessage = async () => {
    if (!paInput.trim() || paChatLoading) return
    const userMsg = paInput.trim()
    setPaInput('')
    const newChat = [...paChat, { role: 'user', content: userMsg }]
    setPaChat(newChat)
    setPaChatLoading(true)
    try {
      const context = buildPAContext()
      const messagesForApi = [
        { role: 'user', content: `Here is the current OS state for context:\n\n${context}` },
        { role: 'assistant', content: 'Got it. I have the full state. What do you need?' },
        ...newChat,
      ]
      const text = await callClaude(messagesForApi, 700)
      const action = parseActionBlock(text)
      const cleanText = stripActionBlock(text)
      setPaChat(c => [...c, { role: 'assistant', content: cleanText, action }])
    } catch (e) {
      setPaChat(c => [...c, { role: 'assistant', content: 'Connection error - try again.' }])
    }
    setPaChatLoading(false)
  }

  const handleApplyAction = useCallback((messageIdx, action) => {
    const ok = applyPaAction(action)
    setPaChat(c => c.map((m, i) => i === messageIdx ? { ...m, action: null, applied: ok ? 'success' : 'failed' } : m))
  }, [applyPaAction])

  const handleSkipAction = useCallback((messageIdx) => {
    setPaChat(c => c.map((m, i) => i === messageIdx ? { ...m, action: null, applied: 'skipped' } : m))
  }, [])

  const openPA = () => {
    setPaOpen(true)
    if (!paBrief) generatePABrief()
  }

  const visibleIds = new Set()
  const collectVisible = node => {
    visibleIds.add(node.id)
    if (node.expanded && node.children) node.children.forEach(collectVisible)
  }
  collectVisible(tree)

  const all = flatNodes(tree)
  const nodeMap = {}
  all.forEach(n => { nodeMap[n.id] = n })

  const visibleNodes = all.filter(n => visibleIds.has(n.id))
  const visibleConns = getConnections(tree).filter(c => visibleIds.has(c.parentId) && visibleIds.has(c.childId))

  // Tag filter: which nodes are "highlighted" (not dimmed) under current filter
  const highlightedIds = new Set()
  if (tagFilter) {
    const parentMap = {}
    function buildParents(node, parentId) {
      parentMap[node.id] = parentId
      if (node.children) node.children.forEach(c => buildParents(c, node.id))
    }
    buildParents(tree, null)
    all.forEach(n => {
      if (NODE_META[n.id]?.tags?.includes(tagFilter)) {
        let cur = n.id
        while (cur) { highlightedIds.add(cur); cur = parentMap[cur] }
      }
    })
  }

  // Visible cross-links (both endpoints visible)
  const activeCrossLinks = showCrossLinks
    ? CROSS_LINKS.filter(l => visibleIds.has(l.from) && visibleIds.has(l.to))
    : []

  const selected = selectedId ? findNode(tree, selectedId) : null
  const selectedMeta = selectedId ? NODE_META[selectedId] : null

  const modalNode = modal ? findNode(tree, modal.nodeId) : null

  const TAGS = ['TOOL', 'ORG', 'PLAN', 'ACHIEVEMENT', 'LESSON', 'MILESTONE', 'TEAM']
  const TAG_COLORS = { TOOL: '#7C8CF8', ORG: '#00E5A0', PLAN: '#FFD166', ACHIEVEMENT: '#E8A0FF', LESSON: '#FF8C66', MILESTONE: '#4FC3F7', TEAM: '#FFC857' }

  return (
    <div style={{ width: '100%', height: '100%', background: '#060606', position: 'relative', overflow: 'hidden' }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=Instrument+Sans:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');`}</style>

      {/* Top toolbar: tag filter + reset buttons */}
      <div style={{ position: 'absolute', top: 14, left: 14, right: 14, zIndex: 20, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: '#4A4E58', fontFamily: "'JetBrains Mono', monospace", marginRight: 4 }}>FILTER:</span>
          <button onClick={() => setTagFilter(null)} style={{ background: !tagFilter ? 'rgba(0,229,160,0.12)' : 'transparent', border: `1px solid ${!tagFilter ? 'rgba(0,229,160,0.35)' : '#1E2028'}`, color: !tagFilter ? '#00E5A0' : '#6B7080', borderRadius: 14, padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>
            All
          </button>
          {TAGS.map(t => (
            <button key={t} onClick={() => setTagFilter(tagFilter === t ? null : t)} style={{
              background: tagFilter === t ? `${TAG_COLORS[t]}20` : 'transparent',
              border: `1px solid ${tagFilter === t ? TAG_COLORS[t] : '#1E2028'}`,
              color: tagFilter === t ? TAG_COLORS[t] : '#6B7080',
              borderRadius: 14, padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit',
            }}>{t}</button>
          ))}
          <button onClick={() => setShowCrossLinks(v => !v)} title="Toggle cross-link dotted lines" style={{ background: showCrossLinks ? 'rgba(124,140,248,0.12)' : 'transparent', border: `1px solid ${showCrossLinks ? 'rgba(124,140,248,0.35)' : '#1E2028'}`, color: showCrossLinks ? '#7C8CF8' : '#6B7080', borderRadius: 14, padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', marginLeft: 6 }}>
            ↔ Connections
          </button>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={fetchOsState} disabled={osStateLoading} title={osStateRemote?.updatedAt ? `Auto-sync last updated ${osStateRemote.updatedAt}. Click to refresh.` : 'Click to fetch latest auto-sync from box.'} style={{ background: osStateRemote ? 'rgba(0,229,160,0.06)' : 'transparent', border: `1px solid ${osStateRemote ? 'rgba(0,229,160,0.3)' : '#2A2E38'}`, borderRadius: 8, color: osStateLoading ? '#3A3E4A' : osStateRemote ? '#00E5A0' : '#6B7080', padding: '7px 12px', fontSize: 11, cursor: osStateLoading ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
            {osStateLoading ? '...' : osStateRemote?.updatedAt ? `↻ ${formatRelativeTime(osStateRemote.updatedAt)}` : '↻ Sync'}
          </button>
          <button onClick={openPA} title="Open the AI Personal Assistant panel" style={{ background: 'linear-gradient(135deg, rgba(0,229,160,0.15), rgba(124,140,248,0.15))', border: '1px solid rgba(0,229,160,0.4)', borderRadius: 8, color: '#00E5A0', padding: '7px 14px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>
            ✦ PA
          </button>
          <button onClick={fitToView} title="Auto-zoom to fit visible tree" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid #2A2E38', borderRadius: 8, color: '#8A8F98', padding: '7px 14px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
            ⊡ Fit
          </button>
          <button onClick={() => setAllExpanded(true)} title="Expand all nodes" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid #2A2E38', borderRadius: 8, color: '#8A8F98', padding: '7px 14px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
            + All
          </button>
          <button onClick={() => setAllExpanded(false)} title="Collapse all branches" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid #2A2E38', borderRadius: 8, color: '#8A8F98', padding: '7px 14px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
            − All
          </button>
          <button onClick={resetLayout} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid #2A2E38', borderRadius: 8, color: '#8A8F98', padding: '7px 14px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
            Reset Layout
          </button>
          <button onClick={resetToDefault} style={{ background: 'rgba(255,107,107,0.07)', border: '1px solid rgba(255,107,107,0.2)', borderRadius: 8, color: '#FF6B6B', padding: '7px 14px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
            Reset to Default
          </button>
        </div>
      </div>

      <div style={{ position: 'absolute', bottom: 20, right: 14, zIndex: 20, display: 'flex', flexDirection: 'column', gap: 5 }}>
        {['+', '−', '⊡'].map((icon, i) => (
          <button key={i} onClick={() => {
            if (i === 0) setTransform(p => ({ ...p, scale: Math.min(3, p.scale * 1.2) }))
            else if (i === 1) setTransform(p => ({ ...p, scale: Math.max(0.1, p.scale * 0.83) }))
            else {
              const el = containerRef.current
              if (!el) return
              const { width, height } = el.getBoundingClientRect()
              setTransform({ x: width / 2 - 2900 * 0.6, y: height * 0.04, scale: 0.6 })
            }
          }} style={{ width: 32, height: 32, background: 'rgba(255,255,255,0.05)', border: '1px solid #2A2E38', borderRadius: 6, color: '#8A8F98', fontSize: i < 2 ? 20 : 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {icon}
          </button>
        ))}
      </div>

      <div style={{ position: 'absolute', bottom: 20, left: 14, zIndex: 20, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {[['ACTIVE / LIVE', '#00E5A0'], ['BUILDING', '#FFD166'], ['PLANNED', '#6B7080'], ['FUTURE / OPEN', '#3A3E4A']].map(([label, color]) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 7, height: 7, borderRadius: '50%', background: color }} />
            <span style={{ fontSize: 10, color: '#4A4E58', fontFamily: "'JetBrains Mono', monospace" }}>{label}</span>
          </div>
        ))}
      </div>

      <div
        ref={containerRef}
        style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}
        onPointerDown={handleCanvasPointerDown}
      >
        <div style={{
          position: 'absolute', transformOrigin: '0 0',
          transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
        }}>
          <svg style={{ position: 'absolute', inset: 0, width: 6000, height: 4000, pointerEvents: 'none', overflow: 'visible' }}>
            {/* Parent-child connections */}
            {visibleConns.map(({ parentId, childId }) => {
              const pp = positions[parentId], cp = positions[childId]
              const pd = DIM[nodeMap[parentId]?.type] || DIM.leaf
              const cd = DIM[nodeMap[childId]?.type] || DIM.leaf
              if (!pp || !cp) return null
              const x1 = pp.x + pd.w / 2, y1 = pp.y + pd.h
              const x2 = cp.x + cd.w / 2, y2 = cp.y
              const my = (y1 + y2) / 2
              const dimmed = tagFilter && (!highlightedIds.has(parentId) || !highlightedIds.has(childId))
              return (
                <path
                  key={`${parentId}-${childId}`}
                  d={`M ${x1} ${y1} C ${x1} ${my}, ${x2} ${my}, ${x2} ${y2}`}
                  fill="none" stroke="#d4a855" strokeOpacity={dimmed ? 0.06 : 0.19} strokeWidth={1.5}
                />
              )
            })}
            {/* Cross-links (dotted, the "how dots connect" layer) */}
            {activeCrossLinks.map(({ from, to, label }) => {
              const fp = positions[from], tp = positions[to]
              const fd = DIM[nodeMap[from]?.type] || DIM.leaf
              const td = DIM[nodeMap[to]?.type] || DIM.leaf
              if (!fp || !tp) return null
              const x1 = fp.x + fd.w / 2, y1 = fp.y + fd.h / 2
              const x2 = tp.x + td.w / 2, y2 = tp.y + td.h / 2
              const dimmed = tagFilter && (!highlightedIds.has(from) || !highlightedIds.has(to))
              const isSelected = selectedId && (selectedId === from || selectedId === to)
              const stroke = isSelected ? '#00E5A0' : '#7C8CF8'
              const opacity = dimmed ? 0.08 : isSelected ? 0.85 : 0.35
              return (
                <g key={`xl-${from}-${to}`} opacity={opacity}>
                  <path
                    d={`M ${x1} ${y1} Q ${(x1 + x2) / 2} ${Math.min(y1, y2) - 90}, ${x2} ${y2}`}
                    fill="none" stroke={stroke} strokeWidth={1.2} strokeDasharray="4 4"
                  />
                </g>
              )
            })}
          </svg>

          {visibleNodes.map(node => {
            const pos = positions[node.id]
            if (!pos) return null
            const meta = NODE_META[node.id]
            const dimmed = tagFilter && !highlightedIds.has(node.id)
            return (
              <MapNode
                key={node.id}
                node={node}
                pos={pos}
                meta={meta}
                remoteLastUsed={osStateRemote?.toolLastUsed?.[node.id]}
                dimmed={dimmed}
                selected={selectedId === node.id}
                onDragStart={handleNodeDragStart}
                onClick={id => setSelectedId(id)}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onAdd={handleAdd}
                onToggle={handleToggle}
              />
            )
          })}
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div style={{
          position: 'absolute', top: 0, right: 0, bottom: 0, width: 360, maxWidth: '90vw',
          background: '#0B0D11', borderLeft: '1px solid #1E2028',
          padding: '60px 22px 20px', overflowY: 'auto', zIndex: 30,
          boxShadow: '-12px 0 32px rgba(0,0,0,0.6)',
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        }}>
          <button onClick={() => setSelectedId(null)} style={{ position: 'absolute', top: 16, right: 16, background: 'transparent', border: '1px solid #2A2E38', borderRadius: 6, color: '#8A8F98', padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>✕ Close</button>

          <div style={{ fontSize: 20, fontWeight: 700, color: '#E8E9EC', marginBottom: 4, lineHeight: 1.2 }}>{selected.name.replace('\n', ' ')}</div>
          {selected.sub && <div style={{ fontSize: 13, color: '#6B7080', marginBottom: 14, fontFamily: "'JetBrains Mono', monospace" }}>{selected.sub}</div>}

          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 18 }}>
            <span style={{ fontSize: 10, padding: '3px 9px', borderRadius: 10, background: `${STATUS[selected.status] || '#3A3E4A'}22`, border: `1px solid ${STATUS[selected.status] || '#3A3E4A'}55`, color: STATUS[selected.status] || '#8A8F98', fontWeight: 600 }}>{selected.status}</span>
            {(selectedMeta?.tags || []).map(t => (
              <span key={t} style={{ fontSize: 10, padding: '3px 9px', borderRadius: 10, background: `${TAG_COLORS[t] || '#6B7080'}18`, border: `1px solid ${TAG_COLORS[t] || '#6B7080'}40`, color: TAG_COLORS[t] || '#6B7080', fontWeight: 600 }}>{t}</span>
            ))}
            {isStale(selectedMeta) && <span style={{ fontSize: 10, padding: '3px 9px', borderRadius: 10, background: 'rgba(255,107,107,0.12)', border: '1px solid rgba(255,107,107,0.4)', color: '#FF6B6B', fontWeight: 600 }}>⚠ STALE</span>}
          </div>

          {selectedMeta?.lastUsed && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, color: '#4A4E58', fontWeight: 700, letterSpacing: 0.5, marginBottom: 4 }}>LAST USED</div>
              <div style={{ fontSize: 13, color: '#C4C6CE' }}>{selectedMeta.lastUsed}</div>
            </div>
          )}

          {selectedMeta?.notes && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, color: '#4A4E58', fontWeight: 700, letterSpacing: 0.5, marginBottom: 4 }}>NOTES</div>
              <div style={{ fontSize: 13, color: '#C4C6CE', lineHeight: 1.55 }}>{selectedMeta.notes}</div>
            </div>
          )}

          {selectedMeta?.doc && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, color: '#4A4E58', fontWeight: 700, letterSpacing: 0.5, marginBottom: 4 }}>DOC / LINK</div>
              <a href={selectedMeta.doc.startsWith('http') ? selectedMeta.doc : '#'} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: '#7C8CF8', wordBreak: 'break-all' }}>{selectedMeta.doc}</a>
            </div>
          )}

          {(() => {
            const linksFrom = CROSS_LINKS.filter(l => l.from === selectedId)
            const linksTo = CROSS_LINKS.filter(l => l.to === selectedId)
            if (linksFrom.length === 0 && linksTo.length === 0) return null
            return (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: '#4A4E58', fontWeight: 700, letterSpacing: 0.5, marginBottom: 6 }}>CONNECTIONS</div>
                {linksFrom.map(l => (
                  <div key={`f-${l.to}`} onClick={() => setSelectedId(l.to)} style={{ fontSize: 12, color: '#C4C6CE', padding: '6px 10px', background: '#13151A', borderRadius: 6, marginBottom: 4, cursor: 'pointer', borderLeft: '2px solid #7C8CF8' }}>
                    <span style={{ color: '#6B7080' }}>{l.label} →</span> {nodeMap[l.to]?.name.replace('\n', ' ') || l.to}
                  </div>
                ))}
                {linksTo.map(l => (
                  <div key={`t-${l.from}`} onClick={() => setSelectedId(l.from)} style={{ fontSize: 12, color: '#C4C6CE', padding: '6px 10px', background: '#13151A', borderRadius: 6, marginBottom: 4, cursor: 'pointer', borderLeft: '2px solid #00E5A0' }}>
                    <span style={{ color: '#6B7080' }}>← {l.label}</span> {nodeMap[l.from]?.name.replace('\n', ' ') || l.from}
                  </div>
                ))}
              </div>
            )
          })()}

          <div style={{ display: 'flex', gap: 8, marginTop: 22, paddingTop: 16, borderTop: '1px solid #1E2028' }}>
            <button onClick={() => { handleEdit(selectedId); setSelectedId(null) }} style={{ flex: 1, background: 'rgba(124,140,248,0.1)', border: '1px solid rgba(124,140,248,0.3)', borderRadius: 8, color: '#7C8CF8', padding: '8px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Edit</button>
            {selected.type !== 'leaf' && <button onClick={() => { handleAdd(selectedId); setSelectedId(null) }} style={{ flex: 1, background: 'rgba(0,229,160,0.1)', border: '1px solid rgba(0,229,160,0.3)', borderRadius: 8, color: '#00E5A0', padding: '8px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Add Child</button>}
          </div>
        </div>
      )}

      {/* PA Panel (Phase 3) */}
      {paOpen && (
        <div style={{
          position: 'absolute', top: 0, right: selected ? 360 : 0, bottom: 0, width: 440, maxWidth: '92vw',
          background: '#0B0D11', borderLeft: '1px solid #1E2028',
          padding: '60px 22px 20px', overflowY: 'auto', zIndex: 35,
          boxShadow: '-12px 0 32px rgba(0,0,0,0.6)',
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          display: 'flex', flexDirection: 'column',
        }}>
          <button onClick={() => setPaOpen(false)} style={{ position: 'absolute', top: 16, right: 16, background: 'transparent', border: '1px solid #2A2E38', borderRadius: 6, color: '#8A8F98', padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>✕ Close</button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 18, color: '#00E5A0' }}>✦</span>
            <span style={{ fontSize: 18, fontWeight: 700, color: '#E8E9EC' }}>Personal Assistant</span>
          </div>
          <div style={{ fontSize: 11, color: '#6B7080', marginBottom: 18, fontFamily: "'JetBrains Mono', monospace" }}>Claude-powered, full OS context</div>

          {/* Today's Brief */}
          <div style={{ background: 'rgba(124,140,248,0.06)', border: '1px solid rgba(124,140,248,0.2)', borderRadius: 12, padding: 14, marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#7C8CF8', letterSpacing: 0.5 }}>✦ TODAY'S BRIEF</div>
              <button onClick={generatePABrief} disabled={paBriefLoading} style={{ background: 'transparent', border: '1px solid #2A2E38', borderRadius: 6, color: paBriefLoading ? '#3A3E4A' : '#8A8F98', padding: '3px 9px', fontSize: 10, cursor: paBriefLoading ? 'wait' : 'pointer' }}>
                {paBriefLoading ? '...' : '↻ Refresh'}
              </button>
            </div>
            {paBriefLoading && !paBrief && <div style={{ fontSize: 12, color: '#6B7080' }}>Reading your OS state...</div>}
            {paBrief && (
              <div style={{ fontSize: 13, color: '#E8E9EC', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{paBrief.text}</div>
            )}
          </div>

          {/* Chat */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10, minHeight: 200 }}>
            <div style={{ fontSize: 10, color: '#4A4E58', fontWeight: 700, letterSpacing: 0.5 }}>CHAT</div>
            {paChat.length === 0 && !paChatLoading && (
              <div style={{ fontSize: 12, color: '#6B7080', fontStyle: 'italic', padding: '8px 0' }}>
                Ask anything: "what's stale", "what should I do next", "summarize Day 25 -&gt; today", "which tools haven't I used in 2 weeks"...
              </div>
            )}
            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8, paddingRight: 4 }}>
              {paChat.map((m, i) => (
                <React.Fragment key={i}>
                  <div style={{
                    background: m.role === 'user' ? 'rgba(0,229,160,0.08)' : '#13151A',
                    border: `1px solid ${m.role === 'user' ? 'rgba(0,229,160,0.2)' : '#1E2028'}`,
                    borderRadius: 10, padding: '10px 12px',
                    fontSize: 13, color: '#E8E9EC', lineHeight: 1.5, whiteSpace: 'pre-wrap',
                    alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                    maxWidth: '90%',
                  }}>{m.content}</div>
                  {m.action && (
                    <div style={{
                      background: 'rgba(255,209,102,0.06)', border: '1px solid rgba(255,209,102,0.3)', borderRadius: 10,
                      padding: '10px 12px', fontSize: 12, color: '#FFD166', alignSelf: 'flex-start', maxWidth: '90%',
                    }}>
                      <div style={{ fontSize: 10, fontWeight: 700, marginBottom: 6, letterSpacing: 0.5 }}>⚡ PROPOSED ACTION</div>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: '#C4C6CE', marginBottom: 8, wordBreak: 'break-word' }}>
                        {m.action.action} {m.action.id || m.action.parentId} {m.action.changes ? JSON.stringify(m.action.changes) : m.action.node ? JSON.stringify(m.action.node) : ''}
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => handleApplyAction(i, m.action)} style={{ flex: 1, background: 'linear-gradient(135deg, #00E5A0, #7C8CF8)', border: 'none', borderRadius: 6, color: '#0B0D11', padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Apply</button>
                        <button onClick={() => handleSkipAction(i)} style={{ flex: 1, background: 'transparent', border: '1px solid #2A2E38', borderRadius: 6, color: '#8A8F98', padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}>Skip</button>
                      </div>
                    </div>
                  )}
                  {m.applied === 'success' && (
                    <div style={{ fontSize: 11, color: '#00E5A0', alignSelf: 'flex-start', padding: '2px 8px' }}>✓ Applied</div>
                  )}
                  {m.applied === 'failed' && (
                    <div style={{ fontSize: 11, color: '#FF6B6B', alignSelf: 'flex-start', padding: '2px 8px' }}>✕ Apply failed (node id may not exist)</div>
                  )}
                  {m.applied === 'skipped' && (
                    <div style={{ fontSize: 11, color: '#6B7080', alignSelf: 'flex-start', padding: '2px 8px' }}>— Skipped</div>
                  )}
                </React.Fragment>
              ))}
              {paChatLoading && (
                <div style={{ fontSize: 12, color: '#6B7080', alignSelf: 'flex-start', padding: '4px 8px' }}>Thinking...</div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, paddingTop: 8, borderTop: '1px solid #1E2028' }}>
              <input
                type="text"
                value={paInput}
                onChange={e => setPaInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && sendPAMessage()}
                placeholder="Ask the PA..."
                disabled={paChatLoading}
                style={{ flex: 1, background: '#1A1D23', border: '1px solid #2A2E38', borderRadius: 8, color: '#E8E9EC', padding: '8px 12px', fontSize: 13, outline: 'none', fontFamily: 'inherit' }}
              />
              <button onClick={sendPAMessage} disabled={!paInput.trim() || paChatLoading} style={{ background: paInput.trim() && !paChatLoading ? 'linear-gradient(135deg, #00E5A0, #7C8CF8)' : '#1A1D23', border: 'none', borderRadius: 8, color: paInput.trim() && !paChatLoading ? '#0B0D11' : '#6B7080', padding: '0 14px', fontSize: 13, fontWeight: 700, cursor: paInput.trim() && !paChatLoading ? 'pointer' : 'default' }}>Send</button>
            </div>
          </div>
        </div>
      )}

      {modal?.type === 'add' && modalNode && <AddModal parentNode={modalNode} onAdd={confirmAdd} onClose={() => setModal(null)} />}
      {modal?.type === 'edit' && modalNode && <EditModal node={modalNode} onSave={confirmEdit} onClose={() => setModal(null)} />}
      {modal?.type === 'delete' && modalNode && <DeleteModal node={modalNode} onConfirm={confirmDelete} onClose={() => setModal(null)} />}
    </div>
  )
}

export default BlessedOpsMindMapDraggable
